import React, { useEffect, useState } from "react";
import API from '../../services/api';
import CookieManager from "../../services/CookieManager";
import { ErrorToast, SuccessToast } from '../../pure-components/CustomToast';
import { toast } from 'react-toastify';
import { CircularProgress, TextField } from '@material-ui/core';
import { DateRangePicker } from "materialui-daterange-picker";

const ExportLicenseComp = () => {
    const [totalLicenseCount, setTotalLicenseCount] = useState(-1);
    const [licenseCountLoading, setLicenseCountLoading] = useState(true);
    const [exportingLicenses, setExportingLicenses] = useState(false);
    const [startDate, setStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 6)));
    const [endDate, setEndDate] = useState(new Date());
    const [dateRangeVisible, setDateRangeVisible] = useState(false);
    const [dateRange, setDateRange] = useState({ startDate: startDate, endDate: endDate });

    const toggle = () => setDateRangeVisible(!dateRangeVisible);
    const setDateRangeValues = (dateRange) => {
        setStartDate(new Date(dateRange.startDate));
        setEndDate(new Date(dateRange.endDate));
        setDateRange(dateRange);
    };

    useEffect(() => {
        (async () => {
            await API.getTotalLicenseCount().then(res => {
                if (res.success) {
                    setTotalLicenseCount(res.data.licenseCount);
                }
                setLicenseCountLoading(false);
            }).catch((err) => {
                setLicenseCountLoading(false);
                toast(({ closeToast }) => <ErrorToast message={`Error : ${err.status}: ${err?.message}`} />);
            });
        })();
    }, []);

    const exportLicenses = async () => {
        setExportingLicenses(true);
        const xAuth = CookieManager.getCookie("x-auth");
        const xAccessToken = CookieManager.getCookie("x-access-token");
        const url = `${process.env.REACT_APP_BASE_URL}api/license/download-all`;

        await fetch(url, {
            method: "post",
            headers: {
                "Content-Type": "application/json",
                "x-auth": xAuth,
                "x-access-token": xAccessToken,
            },
            body: JSON.stringify({ startDate: startDate, endDate: endDate })
        }).then(function (res) {
            if (res.status === 200) {
                res.json().then(function (resbody) {
                    if (resbody.success)
                        window.open(resbody.downloadUrl, '_blank');
                    else
                        toast(({ closeToast }) => <ErrorToast message={`Error : ${resbody.code}: ${resbody?.message}`} />);
                });
            } else {
                res.json().then(function (resbody) {
                    toast(({ closeToast }) => <ErrorToast message={`Error : ${resbody.status}: ${resbody?.statusText} ${res?.mssg}`} />);
                });
                toast(({ closeToast }) => <ErrorToast message={`Error : ${res.status}: ${res?.statusText}`} />);
            }
            setExportingLicenses(false);
        }).catch((err) => {
            setExportingLicenses(false);
            toast(({ closeToast }) => <ErrorToast message={`Error : ${err.status}: ${err?.message}`} />);
        });
    };

    return (
        <div className="bg-white shadow-xl rounded-2xl p-4 sm:p-6 m-4 border border-gray-100">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent mb-2">
                    Export License Data
                </h2>
                <p className="text-gray-600 text-sm">
                    Download license reports for a specific date range
                </p>
            </div>

            <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-6">
                {/* License Count Section */}
                <div className="flex-shrink-0 w-full lg:w-auto flex justify-center lg:justify-start">
                    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-6 border border-indigo-100">
                        <div className="text-center">
                            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-full mb-3 mx-auto shadow-lg">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-sm font-semibold text-gray-600 mb-2">Total Licenses</h3>
                            {licenseCountLoading ? (
                                <CircularProgress size={24} className="text-indigo-600" />
                            ) : totalLicenseCount < 0 ? (
                                <span className="text-red-500 font-bold text-lg">N/A</span>
                            ) : (
                                <div className="flex flex-col items-center">
                                    <span className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
                                        {totalLicenseCount.toLocaleString()}
                                    </span>
                                    <span className="text-xs text-gray-500 mt-1">records available</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Export Section */}
                <div className="flex-1 w-full">
                    {exportingLicenses ? (
                        <div className="flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
                            <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-4 shadow-lg animate-pulse">
                                <CircularProgress size={32} className="text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Processing Export</h3>
                            <p className="text-gray-600 text-center">
                                Please wait while we prepare your license data...
                            </p>
                            <div className="mt-4 w-48 bg-gray-200 rounded-full h-2 overflow-hidden">
                                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full animate-pulse"></div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Select Date Range</h3>
                            
                            {/* Date Range Inputs */}
                            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                                <div className="flex-1">
                                    <label className="block text-sm font-semibold text-gray-600 mb-2">
                                        From Date
                                    </label>
                                    <div className="relative">
                                        <TextField
                                            value={startDate.toLocaleDateString('pt-PT')}
                                            onClick={toggle}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            className="bg-white"
                                            InputProps={{
                                                className: "text-base border-gray-300 rounded-xl hover:border-indigo-400 focus:border-indigo-600 transition-all duration-200",
                                                readOnly: true,
                                                startAdornment: (
                                                    <div className="mr-2">
                                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                    </div>
                                                ),
                                            }}
                                        />
                                    </div>
                                </div>
                                
                                <div className="flex-1">
                                    <label className="block text-sm font-semibold text-gray-600 mb-2">
                                        To Date
                                    </label>
                                    <div className="relative">
                                        <TextField
                                            value={endDate.toLocaleDateString('pt-PT')}
                                            onClick={toggle}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            className="bg-white"
                                            InputProps={{
                                                className: "text-base border-gray-300 rounded-xl hover:border-indigo-400 focus:border-indigo-600 transition-all duration-200",
                                                readOnly: true,
                                                startAdornment: (
                                                    <div className="mr-2">
                                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                    </div>
                                                ),
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>


                            {/* Export Button */}
                            <div className="flex justify-end">
                                <button
                                    onClick={exportLicenses}
                                    className="group relative bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold px-8 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-indigo-200"
                                >
                                    <div className="flex items-center gap-3">
                                        <svg className="w-5 h-5 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span>Export Licenses</span>
                                    </div>
                                    
                                    {/* Button shimmer effect */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                </button>
                            </div>

                            {/* Date range info */}
                            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                                <div className="flex items-center gap-2 text-sm text-blue-700">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>
                                        Selected range: {Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))} days
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Date Range Picker */}
            {dateRangeVisible && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-full max-h-full overflow-auto">
                        <DateRangePicker
                            open={dateRangeVisible}
                            toggle={toggle}
                            onChange={(range) => setDateRangeValues(range)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};


export default ExportLicenseComp;
